<template>
  <a-modal
    title="出租方函件"
    :width="1500"
    :visible="visible"
    :footer="null"
    :destroyOnClose="true"
    @cancel="handleClose"
  >
    <a-table
      :columns="columns"
      :data-source="data"
      :loading="loading"
      rowKey="id"
      :pagination="false"
    >
      <span slot="electronicSealFile" slot-scope="text, row">
        <a
          v-if="row.electronicSealFileName"
          class="file-link"
          @click="downloadLetter(row, 'ELECTRONIC_SEAL')"
        >
          {{ row.electronicSealFileName }}
        </a>
        <span v-else>--</span>
      </span>
      <span slot="noSealFile" slot-scope="text, row">
        <a
          v-if="row.noSealFileName"
          class="file-link"
          @click="downloadLetter(row, 'NO_SEAL')"
        >
          {{ row.noSealFileName }}
        </a>
        <span v-else>--</span>
      </span>
      <span slot="receiptFile" slot-scope="text, row">
        <span v-if="row.receiptFileName" class="receipt-file-cell">
          <a class="file-link" @click="downloadLetter(row, 'RECEIPT')">
            {{ row.receiptFileName }}
          </a>
          <a-popconfirm
            title="确认删除回执附件？"
            ok-text="确定"
            cancel-text="取消"
            @confirm="deleteReceipt(row)"
          >
            <a class="delete-action">
              <a-icon type="delete" />
            </a>
          </a-popconfirm>
        </span>
        <span v-else>--</span>
      </span>
      <span slot="operation" slot-scope="text, row">
        <a-upload
          action="https://api.jssyjt.net/external-service-center/uploadFile/attachment"
          :headers="uploadHeaders"
          :data="{ uploadType: receiptUploadType }"
          :disabled="loading"
          :showUploadList="false"
          @change="(info) => handleReceiptUploadChange(info, row)"
        >
          <a-button
            class="upload-receipt-btn"
            :loading="row.receiptUploading"
            :disabled="loading"
          >
            <a-icon type="upload" />
            上传回执附件
          </a-button>
        </a-upload>
      </span>
    </a-table>
    <div class="modal-footer-center">
      <a-button @click="handleClose">关闭</a-button>
    </div>
  </a-modal>
</template>

<script>
import storage from "store";
import { ACCESS_TOKEN } from "@/store/mutation-types";
import {
  deleteProjectLetterReceipt,
  downloadProjectLetter,
  listProjectLetter,
  saveProjectLetterReceipt,
} from "@/api/project/Project.js";

const PROJECT_RECEIPT_UPLOAD_TYPE = 703;

export default {
  name: "ProjectLetterModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    record: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      data: [],
      receiptUploadType: PROJECT_RECEIPT_UPLOAD_TYPE,
      columns: [
        { title: "函件名称", dataIndex: "letterName", width: 320 },
        {
          title: "电子签章函件下载",
          width: 260,
          scopedSlots: { customRender: "electronicSealFile" },
        },
        {
          title: "无签章函件下载",
          width: 260,
          scopedSlots: { customRender: "noSealFile" },
        },
        {
          title: "回执附件",
          width: 260,
          scopedSlots: { customRender: "receiptFile" },
        },
        {
          title: "操作",
          width: 180,
          align: "center",
          scopedSlots: { customRender: "operation" },
        },
      ],
    };
  },
  computed: {
    uploadHeaders() {
      const token = storage.get(ACCESS_TOKEN);
      return {
        Authorization: token || "",
      };
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.data = [];
        this.getList();
      }
    },
  },
  methods: {
    getList() {
      const webId = (this.record || {}).webId;
      if (!webId) return Promise.resolve();

      this.loading = true;
      return listProjectLetter({ webId })
        .then((res) => {
          this.data = Array.isArray(res.data) ? res.data : [];
        })
        .catch((err) => {
          this.data = [];
          this.$notification["error"]({
            message: "项目函件查询失败",
            description: this.getErrorMessage(err),
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    downloadLetter(row, fileType) {
      if (!row || !row.id || !fileType) return;
      downloadProjectLetter({ id: row.id, fileType })
        .then((blob) => {
          this.saveBlob(blob, this.getLetterFileName(row, fileType));
        })
        .catch((err) => {
          this.$notification["error"]({
            message: "项目函件下载失败",
            description: this.getErrorMessage(err),
          });
        });
    },

    handleReceiptUploadChange(info, row) {
      if (!row || !row.id) return;
      const { file } = info;
      if (!file) return;

      if (file.status === "uploading") {
        this.$set(row, "receiptUploading", true);
        return;
      }

      if (file.status === "done") {
        const response = file.response || {};
        if (String(response.code) !== "200" || !response.data) {
          this.$set(row, "receiptUploading", false);
          this.$notification["error"]({
            message: "回执附件上传失败",
            description:
              response.msg || response.message || "OSS 未返回有效文件地址",
          });
          return;
        }

        this.saveReceipt(row, {
          fileName: file.name,
          fileUrl: response.data,
        });
        return;
      }

      if (file.status === "error") {
        this.$set(row, "receiptUploading", false);
        this.$notification["error"]({
          message: "回执附件上传失败",
          description:
            file.error && file.error.message
              ? file.error.message
              : "网络异常，请稍后再试",
        });
      }
    },

    saveReceipt(row, file) {
      const webId = row.webId || (this.record || {}).webId;
      this.loading = true;
      saveProjectLetterReceipt({
        id: row.id,
        webId,
        receiptFileName: file.fileName,
        receiptFileUrl: file.fileUrl,
      })
        .then(() => {
          this.$message.success("回执附件保存成功");
          return this.getList();
        })
        .catch((err) => {
          this.$notification["error"]({
            message: "回执附件保存失败",
            description: this.getErrorMessage(err),
          });
        })
        .finally(() => {
          this.$set(row, "receiptUploading", false);
          this.loading = false;
        });
    },

    deleteReceipt(row) {
      const webId = row.webId || (this.record || {}).webId;
      if (!row || !row.id || !webId) return;

      this.loading = true;
      deleteProjectLetterReceipt({
        id: row.id,
        webId,
      })
        .then(() => {
          this.$message.success("回执附件删除成功");
          return this.getList();
        })
        .catch((err) => {
          this.$notification["error"]({
            message: "回执附件删除失败",
            description: this.getErrorMessage(err),
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getLetterFileName(row = {}, fileType) {
      const fileMap = {
        ELECTRONIC_SEAL: row.electronicSealFileName,
        NO_SEAL: row.noSealFileName,
        RECEIPT: row.receiptFileName,
      };
      return fileMap[fileType] || "项目函件";
    },

    handleClose() {
      this.$emit("close");
    },

    saveBlob(blob, fileName) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    getErrorMessage(err) {
      return (
        ((err.response || {}).data || {}).msg ||
        ((err.response || {}).data || {}).message ||
        err.msg ||
        err.message ||
        "网络异常，请稍后再试"
      );
    },
  },
};
</script>

<style scoped>
.file-link {
  color: #1890ff;
  font-weight: 500;
}

.receipt-file-cell {
  display: inline-flex;
  align-items: center;
  max-width: 360px;
  line-height: 22px;
}

.receipt-file-cell .file-link {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-break: break-all;
}

.upload-receipt-btn {
  min-width: 120px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #595959;
  border-color: #d9d9d9;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.upload-receipt-btn .anticon {
  margin-right: 6px;
  color: #8c8c8c;
}

.upload-receipt-btn:hover,
.upload-receipt-btn:focus {
  color: #1890ff;
  border-color: #1890ff;
  background: #fff;
}

.upload-receipt-btn:hover .anticon,
.upload-receipt-btn:focus .anticon {
  color: #1890ff;
}

.modal-footer-center {
  margin-top: 28px;
  text-align: center;
}

.modal-footer-center .ant-btn {
  min-width: 120px;
}

.delete-action {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-left: 8px;
  color: #8c8c8c;
  border-radius: 2px;
  transition: color 0.2s, background 0.2s;
}

.delete-action:hover {
  color: #ff4d4f;
  background: #fff1f0;
}
</style>
